// historyGeneration.js — pure helpers for produce-on-write recommendation
// history persistence (Commit 1). No React, no network — unit-testable in
// isolation. The React layer (App.jsx) owns the Set/maps via refs and the
// actual fetch calls; this module owns the *decisions* and *entry shape*.

// Mint a unique id at request time for one recommendation generation.
export function makeGenerationId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Synchronous dedupe claim. Returns true exactly once per generationId:
// the first caller "claims" it (and it's added to writtenSet BEFORE any
// async POST), every subsequent caller for the same id gets false. This is
// what defeats the async double-insert race — the claim is synchronous.
export function claimGeneration(writtenSet, generationId) {
  if (!generationId) return false;
  if (writtenSet.has(generationId)) return false;
  writtenSet.add(generationId);
  return true;
}

// Map a produced wine object to the persisted per-wine shape. Produce-time
// interactions are zeroed (tried:false, rating:0, tags:[]); they are filled
// later by the interaction-time UPDATE path.
export function mapWine(w, { tried = false, rating = 0, tags = [] } = {}) {
  return {
    producer: w.producer,
    wine: w.wine,
    vintage: w.vintage,
    tried,
    rating,
    tags,
    confidence: w.confidence || null,
    description: w.description || null,
    pairingReason: w.pairingReason || null,
    styleTags: w.styleTags || [],
    price: w.price || null,
    region: w.region || null,
    varietal: w.varietal || null,
  };
}

// Build the produce-time (create) history entry. Mirrors the verbatim shape
// the old exit-time writer used, but with interactions zeroed and reviewed
// false, and stamped with generationId for client-side row matching.
export function buildProduceEntry({ generationId, date, meal, occasion, context, wines }) {
  return {
    generationId,
    date,
    meal,
    occasion,
    context,
    wines: (wines || []).filter(Boolean).map(w => mapWine(w)),
    reviewed: false,
  };
}

// Build the wines array + reviewed flag for an interaction-time UPDATE, from
// the current pairings and the per-index interaction state.
export function buildUpdateWines(pairings, ratings, triedIt, reviewTags) {
  const wines = (pairings || []).filter(Boolean).map((w, i) =>
    mapWine(w, { tried: triedIt[i] || false, rating: ratings[i] || 0, tags: reviewTags[i] || [] })
  );
  const reviewed =
    Object.keys(triedIt || {}).length > 0 ||
    Object.values(ratings || {}).some(r => r > 0);
  return { wines, reviewed };
}
